import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  UploadInput,
  DropDownInput,
  AddressInputField,
  InputField,
  Button,
  DateInput,
} from "@/components";
import { IoArrowForward } from "react-icons/io5";
import { statesOptionsArray } from "../../../utils/Data";
import { personalDetailsSchema } from "../../../schemas";
import { useFieldError, useValidation } from "@/";
import { BiSolidEditAlt } from "react-icons/bi";
import { PhotoViewProvider } from "../../../components";

const PersonalDetails = () => {
  const {
    handleNextStep,
    formData,
    setFormData,
    isDisabled,
    handleIsEdit,
    handleIsCancel,
    kycStatus,
  } = useOutletContext();
  const { validate, errors } = useValidation(personalDetailsSchema);
  const [formErrors, setFormErrors] = useState(errors);
  const getErrorMessage = useFieldError(formErrors || []);
  const status = kycStatus;
  // Options for Gender dropdown
  const GenderOption = {
    Male: "Male",
    Female: "Female",
    Other: "Other",
  };

  const genderOptionsArray = Object.keys(GenderOption).map((key) => ({
    label: GenderOption[key],
    value: key,
  }));

  // Options for monthly shipments dropdown
  const ShipmentsRange = {
    10: "10-100",
    100: "100-500",
    500: "500-1000",
    1000: "1000-5000",
    5000: "5000+",
  };

  const monthlyShipmentsOptionsArray = Object.keys(ShipmentsRange).map(
    (key) => ({
      label: ShipmentsRange[key],
      value: key.toString(),
    })
  );

  // Update top-level personalDetails fields inside data
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      personalDetails: {
        ...prev?.personalDetails,
        [field]: value,
      },
    }));
  };

  // Update nested address fields in personalDetails.address
  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      personalDetails: {
        ...prev?.personalDetails,
        address: {
          ...prev?.personalDetails.address,
          [field]: value,
        },
      },
    }));
  };

  // Update top-level file uploads for aadhar and pan photos
  const handleUploadChange = (field, file) => {
    setFormData((prev) => ({
      ...prev,
      kycDocuments: {
        ...prev?.kycDocuments,
        [field]: file,
      },
    }));
  };

  const handleNext = () => {
    if (isDisabled) {
      handleNextStep();
      return;
    }
    setFormErrors([]);
    const data = {
      ...formData.personalDetails,
      aadhar: formData.aadhar,
      pan: formData.pan,
    };
    const result = validate(data);

    if (!result.isValid) {
      console.log("Validation errors:", result.errors);
      setFormErrors(result.errors);
      return;
    }

    console.log("Personal data:", result.data);
    handleNextStep();
  };
  const handleEdit = () => {
    handleIsEdit();
  };
  const handleCancel = () => {
    handleIsCancel();
  };
  return (
    <div className="flex flex-col gap-4 md:gap-4 w-full p-2">
      <h2 className="md:text-headline3 text-headline4 font-nunito text-secondary-90 font-bold">
        Personal Details
      </h2>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="flex flex-col gap-3 w-full"
      >
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Full Name"
            placeholder="Enter your name"
            type="text"
            value={formData?.personalDetails?.fullName}
            onChange={(val) => handleChange("fullName", val)}
            error={getErrorMessage("fullName")}
            disabled={isDisabled}
          />
          <InputField
            label="Email ID"
            placeholder="Enter your email"
            type="email"
            value={formData.personalDetails?.email}
            onChange={(val) => handleChange("email", val)}
            error={getErrorMessage("email")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Phone No. 1"
            placeholder="Enter your phone number"
            type="number"
            maxLength={10}
            value={formData.personalDetails?.phone1 || ""}
            onChange={(val) => handleChange("phone1", val)}
            error={getErrorMessage("phone1")}
            disabled={isDisabled}
          />
          <InputField
            label="Phone No. 2"
            placeholder="Enter your phone number"
            type="number"
            maxLength={10}
            value={formData.personalDetails?.phone2 || ""}
            onChange={(val) => handleChange("phone2", val)}
            error={getErrorMessage("phone2")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <DateInput
            label="Date Of Birth"
            placeholder="Select Date"
            value={
              formData.personalDetails?.dob
                ? new Date(formData.personalDetails.dob)
                    .toISOString()
                    .split("T")[0]
                : ""
            }
            onChange={(value) =>
              handleChange("dob", new Date(value).toISOString())
            }
            error={getErrorMessage("dob")}
            disabled={isDisabled}
          />

          <DropDownInput
            label="Gender"
            placeholder="Select your gender"
            value={formData.personalDetails?.gender}
            onChange={(value) => handleChange("gender", value)}
            options={genderOptionsArray}
            error={getErrorMessage("gender")}
            disabled={isDisabled}
          />
          <InputField
            label="Pincode"
            placeholder="Enter your pincode"
            type="number"
            maxLength={6}
            value={formData.personalDetails?.address?.pincode}
            onChange={(val) => handleAddressChange("pincode", val)}
            error={getErrorMessage("address.pincode")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="City"
            placeholder="Enter your city"
            type="text"
            value={formData.personalDetails?.address?.city}
            onChange={(val) => handleAddressChange("city", val)}
            error={getErrorMessage("address.city")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="State"
            value={formData.personalDetails?.address?.state}
            onChange={(value) => handleAddressChange("state", value)}
            placeholder="Select your state"
            options={statesOptionsArray}
            error={getErrorMessage("address.state")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <AddressInputField
            label="Address"
            placeholder="Enter your address"
            type="text"
            value={formData.personalDetails?.address?.line1 || ""}
            onChange={(val) => handleAddressChange("line1", val)}
            error={getErrorMessage("address.line1")}
            disabled={isDisabled}
          />
          <DropDownInput
            label="Monthly Shipments"
            value={formData.personalDetails?.monthlyShipments}
            onChange={(value) => handleChange("monthlyShipments", value)}
            options={monthlyShipmentsOptionsArray}
            placeholder="Select monthly order volume"
            error={getErrorMessage("monthlyShipments")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Aadhar No."
            placeholder="Enter your aadhar no."
            maxLength={12}
            value={formData.personalDetails?.aadharNumber || ""}
            onChange={(val) => handleChange("aadharNumber", val)}
            error={getErrorMessage("aadharNumber")}
            disabled={isDisabled}
          />
          <InputField
            label="Pan No."
            placeholder="Enter your pan no."
            maxLength={10}
            value={formData.personalDetails?.panNumber || ""}
            onChange={(val) => handleChange("panNumber", val)}
            error={getErrorMessage("panNumber")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          {isDisabled ? (
            <PhotoViewProvider
              imageURL={formData?.kycDocuments?.aadhar}
              label="Aadhar Card"
            />
          ) : (
            <UploadInput
              label="Aadhar Photo"
              placeholder="Upload Aadhar card photo."
              value={formData.aadhar || null}
              onChange={(val) => handleUploadChange("aadhar", val)}
              note="Note: File type should only be png, jpeg and should be in color"
              noteClassName="text-caption md:text-body1 pr-2"
              spanClassName="text-caption md:text-body1"
              error={getErrorMessage("aadhar")}
              disabled={isDisabled}
            />
          )}

          {isDisabled ? (
            <PhotoViewProvider
              imageURL={formData?.kycDocuments?.pan}
              label="PAN Card"
            />
          ) : (
            <UploadInput
              label="PAN Photo."
              placeholder="Upload PAN card photo."
              value={formData.pan || null}
              onChange={(val) => handleUploadChange("pan", val)}
              note="Note: File type should only be png, jpeg and should be in color"
              noteClassName="text-caption md:text-body1 pr-2"
              spanClassName="text-caption md:text-body1"
              error={getErrorMessage("pan")}
              disabled={isDisabled}
            />
          )}
        </div>
      </form>

      <div className="flex flex-row gap-1 md:gap-2 justify-between sm:justify-end mt-8 mr-2">
        <Button
          size="sm"
          variant="default"
          onClick={isDisabled ? handleEdit : handleCancel}
          icon={BiSolidEditAlt}
        >
          {isDisabled ? "Edit" : "Cancel"}
        </Button>

        <Button
          size="sm"
          variant="default"
          onClick={handleNext}
          icon={IoArrowForward}
        >
          {isDisabled ? "Next" : "Save And Next"}
        </Button>
      </div>
    </div>
  );
};

export default PersonalDetails;
